import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  CartDataService,
  ConfigurationService,
  ProcessedProduct,
  ProductCapabilitiesService,
  ProductProcessingService,
  UserService,
} from '@core';

import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { OrderDetailsAdminPanel, PlayerAddress, ProductDetailsCart, ShippingDetails } from '@models';
import { SkeletonTemplateService } from '@skeleton-loader';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AppleCareListingProductComponent } from '../../../../../ui/apple-components/apple-care-listing-product/apple-care-listing-product.component';

import {
  CartSummaryItemComponent,
  CartTotalInfoComponent,
  PartialHiddenCreditCardComponent,
  PriceAndPointsComponent,
  ShippingInformationComponent,
} from '@shared';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { of, switchMap } from 'rxjs';

@Component({
  selector: 'lib-checkout-order-review',
  templateUrl: './checkout-order-review.component.html',
  styleUrls: ['./checkout-order-review.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    RouterModule,
    MatButtonModule,
    CartTotalInfoComponent,
    CartSummaryItemComponent,
    MatDividerModule,
    MatTooltipModule,
    PartialHiddenCreditCardComponent,
    PriceAndPointsComponent,
    AppleCareListingProductComponent,
    ShippingInformationComponent,
  ],
})
export class CheckoutOrderReviewComponent implements OnInit {
  protected readonly rewardName = inject(ConfigurationService).rewardName;
  productCard = input.required<ProductDetailsCart>();
  shippingAddress = input.required<PlayerAddress | ShippingDetails | OrderDetailsAdminPanel | any>();
  email = input<string>();
  pointsRedeem = input.required<number>();
  pointsAvailable = input.required<number>();
  hidePaymentDetails = input(false);
  hideActionButton = input(false);
  amountLabel = input('Amount due');
  totalLabel = input.required<string>();
  orderNumber = input<string | number>('');
  usdRedeem = input<number>(0);
  displayOnlyShipping = input(false);
  isPlaceOrderPending = input(false);
  cardType = input<string>();

  // New input to indicate when used for order display (capabilities managed by parent)
  isOrderMode = input(false);

  submitAction = output<void>();

  private readonly placeOrderButton = viewChild<ElementRef<HTMLButtonElement>>('placeOrderButton');

  yourItemsLabelTooltip = 'We cannot accept returns or refunds, unless items are damaged or defective.';
  private cartDataService = inject(CartDataService);
  private destroyRef = inject(DestroyRef);

  userService = inject(UserService);
  skeletonService = inject(SkeletonTemplateService);
  private capabilitiesService = inject(ProductCapabilitiesService);
  private processingService = inject(ProductProcessingService);
  isAppleBrand = true;

  processedProducts = computed<ProcessedProduct[]>(() => {
    const products = this.productCard()?.products ?? [];
    return this.processingService.createProcessedProducts(products);
  });

  private readonly processedProducts$ = toObservable(this.processedProducts);
  filteredProcessedProducts = signal<ProcessedProduct[]>([]);

  ngOnInit(): void {
    // Only load capabilities and setup filtering if not in order mode
    // In order mode, the parent component (orders-list) handles capabilities
    if (!this.isOrderMode()) {
      this.cartDataService.fetchProductCart();
      this.loadProductCapabilities();
      this.setupProductFiltering();
    }
  }

  focusPlaceOrder(): void {
    this.placeOrderButton()?.nativeElement.focus();
  }

  loadProductCapabilities(): void {
    const cartProducts = this.cartDataService.productDetailsCart()?.products ?? [];
    this.capabilitiesService.loadProductCapabilities(cartProducts, false); // false for checkout mode
  }

  private setupProductFiltering(): void {
    this.processedProducts$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((processed) => {
          if (processed.length > 0) {
            return this.processingService.filterOutAppleCareProducts(processed);
          } else {
            return of([]);
          }
        }),
      )
      .subscribe((filtered) => {
        this.filteredProcessedProducts.set(filtered);
      });
  }
}
